import { CalendarPlus } from "@phosphor-icons/react";

/**
 * AddToCalendarButton — opens a pre-filled Google Calendar "create event"
 * page in a new tab.
 *
 * No OAuth or backend integration required — Google's public
 * `calendar.google.com/calendar/render?action=TEMPLATE` endpoint accepts
 * a title, start/end datetime pair, description, and location as query
 * parameters. The user confirms and saves inside their own calendar.
 *
 * Props:
 *   title:       event title (required)
 *   start:       ISO 8601 datetime string (required)
 *   end:         ISO 8601 datetime string (optional — defaults to +1h)
 *   description: plain-text details (optional)
 *   location:    address / venue string (optional)
 *   testId:      optional data-testid override
 */
export default function AddToCalendarButton({
  title,
  start,
  end,
  description,
  location,
  testId = "add-to-gcal",
  className = "",
}) {
  if (!title || !start) return null;
  const url = buildGoogleCalendarUrl({ title, start, end, description, location });
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest transition-transform active:scale-95 ${className}`}
      style={{
        border: "1px solid rgba(212,175,55,0.55)",
        color: "#F2DC8C",
        background: "rgba(0,0,0,0.35)",
      }}
    >
      <CalendarPlus size={12} weight="bold" color="#FFD700"/>
      Add to Google Calendar
    </a>
  );
}

/**
 * buildGoogleCalendarUrl — assemble the render-template URL Google
 * expects. Datetimes must be in the compact `YYYYMMDDTHHmmssZ` (UTC)
 * form; we always emit UTC to avoid timezone drift for the user's
 * calendar client (they'll see it in their local zone).
 */
export function buildGoogleCalendarUrl({ title, start, end, description, location }) {
  const startDt = new Date(start);
  const endDt = end ? new Date(end) : new Date(startDt.getTime() + 60 * 60 * 1000);
  const dates = `${toGCalStamp(startDt)}/${toGCalStamp(endDt)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates,
  });
  if (description) params.set("details", description);
  if (location) params.set("location", location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function toGCalStamp(d) {
  // Emits YYYYMMDDTHHmmssZ from a Date object (UTC).
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}
